import { observer } from "mobx-react";
import { useEffect } from "react";
import { Helmet } from "react-helmet";

import Plate from "components/UI/Plate/Plate";
import Button from "components/UI/Button/Button";

import { ApiKeyModal } from "./ApiKeyModal/ApiKeyModal";
import apiKeysStore from "./ApiKeysStore";
import AppStore from "AppStore";

import "./Settings.scss";

/**
 * @param {?string} value ISO-дата с сервера.
 * @returns {string}
 */
export function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const ApiKeyRow = observer(({ item }) => {
  const revoked = Boolean(item.revokedAt);

  return (
    <li className={"settings__key" + (revoked ? " settings__key_revoked" : "")}>
      <div className="settings__key-main">
        <p className="settings__key-name">{item.name}</p>
        <p className="settings__key-prefix">{item.keyPrefix}…</p>
      </div>

      <div className="settings__key-dates">
        <p className="settings__key-date">Выпущен {formatDateTime(item.createdAt)}</p>
        <p className="settings__key-date">
          {item.lastUsedAt
            ? `Последний запрос ${formatDateTime(item.lastUsedAt)}`
            : "Запросов с этим ключом не было"}
        </p>
        {revoked && (
          <p className="settings__key-date settings__key-date_revoked">
            Отозван {formatDateTime(item.revokedAt)}
          </p>
        )}
      </div>

      <div className="settings__key-action">
        {revoked ? (
          <span className="settings__key-status">Отозван</span>
        ) : (
          <Button
            title="Отозвать"
            classes={["outlined", "small"]}
            disabled={apiKeysStore.revokingId !== null}
            onClick={() => apiKeysStore.revokeWithConfirm(item)}
          />
        )}
      </div>
    </li>
  );
});

/**
 * Настройки личного кабинета: пока только ключи публичного API.
 *
 * Страница выдаётся персонально, как и документация API: пункта меню на неё
 * нет (см. закомментированный элемент в DropDownMenu.jsx), в sitemap.xml её
 * тоже нет. Отсюда же noindex: robots.txt запрещает обход, но не индексацию.
 */
export const Settings = observer(() => {
  const isAuth = AppStore.isAuth;

  useEffect(() => {
    window.scroll(0, 0);
    if (isAuth) apiKeysStore.fetch();
  }, [isAuth]);

  const { items, limit, loaded, canCreate } = apiKeysStore;

  return (
    <main className="settings">
      <Helmet>
        <title>Настройки — Boostclick</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="settings__inner">
        <h1 className="settings__title">Настройки</h1>

        <Plate className="settings__section">
          <div className="settings__section-header">
            <div>
              <h2 className="settings__section-title">Ключи API</h2>
              <p className="settings__section-subtitle">
                Ключом подписываются запросы к API Boostclick. Ключ виден целиком
                один раз — в момент выпуска, — потом остаётся только его начало.
              </p>
            </div>
            <Button
              title="Выпустить ключ"
              classes={["small"]}
              disabled={!canCreate}
              onClick={() => apiKeysStore.openCreateModal()}
            />
          </div>

          {!canCreate && (
            <p className="settings__limit">
              Действующих ключей уже {limit} — это максимум. Отзовите ненужный,
              чтобы выпустить новый.
            </p>
          )}

          {loaded && items.length === 0 && (
            <p className="settings__empty">Ключей пока нет.</p>
          )}

          {items.length > 0 && (
            <ul className="settings__keys">
              {items.map((item) => (
                <ApiKeyRow key={item.id} item={item} />
              ))}
            </ul>
          )}
        </Plate>
      </div>

      <ApiKeyModal />
    </main>
  );
});

export default Settings;
